import Swal from 'sweetalert2'

// Device-clock sanity guard.
//
// Some terminals run with a badly-wrong clock — the classic case is a Thai staff
// member setting 11 AM as 11 PM (AM/PM confusion), which puts every timestamp
// exactly ~12h off. A wrong clock silently corrupts business-day bucketing,
// receipt times, running-table timers and close-shop reports, so a terminal with
// a large drift must be treated as UNUSABLE until the operator fixes it.
//
// `useServerTime()` already exposes `offsetMs` = serverTime − deviceClock (from
// Firebase RTDB `.info/serverTimeOffset`). We only add the threshold check + a
// hard, un-dismissable block. NOTE: useServerTime's own heartbeat only catches a
// clock that STEPS while running; a device booted already-wrong produces a stable
// offset, which is exactly what this startup check catches.

const DRIFT_THRESHOLD_MS =  10 * 60_000 // 5 minutes

function humanizeDrift(ms: number): string {
  const totalMin = Math.round(Math.abs(ms) / 60_000)
  if (totalMin < 60) return `${totalMin} นาที / min`
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return m ? `${h} ชม. ${m} นาที / ${h}h ${m}m` : `${h} ชม. / ${h}h`
}

export default defineNuxtPlugin(() => {
  if (!import.meta.client) return

  const serverTime = useServerTime()
  let blocked = false

  const evaluate = (offset: number | null) => {
    if (blocked) return
    // Unknown offset (offline / handshake not resolved) — we cannot verify the
    // clock, so we must NOT block. Offline selling is a supported flow, and the
    // RTDB offset re-anchors on reconnect (the watch below re-checks it then).
    if (offset == null) return
    if (Math.abs(offset) <= DRIFT_THRESHOLD_MS) return

    blocked = true
    const direction = offset < 0
      ? 'เร็วกว่า / ahead of'   // device clock is ahead of real time
      : 'ช้ากว่า / behind'      // device clock is behind real time
    Swal.fire({
      icon: 'error',
      title: 'เวลาของเครื่องไม่ถูกต้อง<br>Device clock is wrong',
      html:
        `นาฬิกาของอุปกรณ์นี้คลาดเคลื่อนจากเวลาจริงประมาณ ` +
        `<b>${humanizeDrift(offset)}</b> (${direction}).<br><br>` +
        `กรุณาแก้ไข <b>วันที่และเวลา</b> ของอุปกรณ์ให้ถูกต้อง ` +
        `(ระวัง AM / PM — 11 โมงเช้า ไม่ใช่ 5 ทุ่ม) แล้วกดโหลดใหม่<br><br>` +
        `<span style="opacity:.7">Please fix this device's date &amp; time (watch AM vs PM), then reload.</span>`,
      confirmButtonText: 'โหลดใหม่ / Reload',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
    }).then(() => {
      window.location.reload()
    })
  }

  // Waits out the RTDB handshake (bounded ~5s) and returns the resolved offset,
  // or null when it couldn't be determined in time / offline.
  serverTime.refresh().then((offset) => {
    if (offset != null) {
      evaluate(offset)
      return
    }
    // Offset not ready at startup (slow/late handshake) — re-check once it lands.
    const stop = watch(serverTime.offsetMs, (v) => {
      if (v != null) {
        stop()
        evaluate(v)
      }
    })
  })
})
